import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getApiKey, setApiKey, clearApiKey } from '../lib/api';
import { Key, Save, Trash2, CheckCircle, AlertTriangle } from 'lucide-react';

export default function Settings() {
  const [apiKey, setApiKeyState] = useState(getApiKey() || '');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  const queryClient = useQueryClient();

  useEffect(() => {
    setApiKeyState(getApiKey() || '');
  }, []);

  const handleSave = () => {
    if (!apiKey.trim()) {
      setError('API Key cannot be empty.');
      return;
    }
    setApiKey(apiKey.trim());
    setSaved(true);
    setError('');
    queryClient.invalidateQueries(); // Refresh all data
    setTimeout(() => setSaved(false), 3000);
  };

  const handleClear = () => {
    clearApiKey();
    setApiKeyState('');
    queryClient.invalidateQueries();
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-100 mb-2">Settings</h1>
        <p className="text-slate-400">
          Configure your connection to the Search Visibility Microservice.
        </p>
      </div>

      <div className="bg-slate-900 border border-slate-800 rounded-lg p-6 space-y-6">
        <div>
          <h2 className="text-lg font-semibold text-slate-100 flex items-center gap-2 mb-4">
            <Key className="h-5 w-5 text-emerald-400" />
            API Authentication
          </h2>
          <p className="text-sm text-slate-400 mb-4">
            Provide an <code className="bg-slate-800 px-1.5 py-0.5 rounded text-slate-200">admin</code> tier API key to access all management features. 
            The key is stored locally in your browser.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="password"
              value={apiKey}
              onChange={(e) => {
                setApiKeyState(e.target.value);
                setSaved(false);
                setError('');
              }}
              placeholder="Enter your Admin API Key"
              className="flex-1 bg-slate-950 border border-slate-700 rounded-md px-3 py-2 text-sm text-slate-200 focus:outline-none focus:ring-2 focus:ring-emerald-500 font-mono"
            />
            <button
              onClick={handleSave}
              className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white font-medium rounded-md transition-colors"
            >
              <Save className="h-4 w-4" />
              Save Key
            </button>
            {apiKey && (
              <button
                onClick={handleClear}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-slate-800 hover:bg-slate-700 text-slate-300 font-medium rounded-md transition-colors"
              >
                <Trash2 className="h-4 w-4" />
                Clear
              </button>
            )}
          </div>

          {error && (
            <div className="mt-3 flex items-center gap-2 text-sm text-red-400">
              <AlertTriangle className="h-4 w-4" />
              {error}
            </div>
          )}

          {saved && (
            <div className="mt-3 flex items-center gap-2 text-sm text-emerald-400">
              <CheckCircle className="h-4 w-4" />
              API Key saved successfully. Data will now load.
            </div>
          )}
        </div>

        <div className="pt-6 border-t border-slate-800">
          <h3 className="text-sm font-semibold text-slate-300 mb-2">Environment Details</h3>
          <div className="bg-slate-950 rounded-md p-4 text-sm font-mono text-slate-400 space-y-1">
            <div className="flex justify-between">
              <span>API Base URL:</span>
              <span className="text-slate-200">{(import.meta as any).env?.VITE_API_BASE_URL || 'https://search-web-quality-production.up.railway.app'}</span>
            </div>
            <div className="flex justify-between">
              <span>Frontend Version:</span>
              <span className="text-slate-200">1.0.0</span>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-slate-100 mb-2">Deployment Instructions</h2>
        <p className="text-sm text-slate-400 mb-4">
          To deploy this admin panel to Cloudflare Pages:
        </p>
        <ol className="list-decimal list-inside space-y-2 text-sm text-slate-400 font-mono">
          <li>Run <code className="bg-slate-800 px-1.5 py-0.5 rounded text-slate-200">npm install</code></li>
          <li>Build the project: <code className="bg-slate-800 px-1.5 py-0.5 rounded text-slate-200">npm run build</code></li>
          <li>Deploy via Wrangler: <code className="bg-slate-800 px-1.5 py-0.5 rounded text-slate-200">npm run deploy</code></li>
        </ol>
        <p className="text-sm text-slate-500 mt-4">
          Alternatively, connect your GitHub repository directly to Cloudflare Pages with the build command <code className="bg-slate-800 px-1.5 py-0.5 rounded text-slate-200">npm run build</code> and output directory <code className="bg-slate-800 px-1.5 py-0.5 rounded text-slate-200">dist</code>.
        </p>
      </div>
    </div>
  );
}
